import React, { useEffect, useMemo, useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { FulfillmentDetailsDrawer } from '../common/FulfillmentDrawer';
import {
  ActiveOrdersTablePresentational,
  ActiveOrdersRequestPayload
} from '../ActiveOrdersTablePresentational/ActiveOrdersTablePresentational';
import { FacetGroup, FacetSelections, SegmentedFacetOption } from '../common/types';
import { useActiveOrdersQuery } from '../../data/useActiveOrdersQuery';
import {
  ACTIVE_ORDERS_DATE_SENTINEL,
  ActiveOrdersQueryVariables,
  ActiveOrdersRequestFilter
} from '../../data/activeOrdersDataModel';
import { buildActiveOrdersDateRangePresets, buildDefaultActiveOrdersDateRange } from '../../data/dateRangePresets';
import {
  EMPTY_ACTIVE_ORDER_FILTER_OPTIONS,
  useActiveOrderFilterOptionsQuery
} from '../../data/useActiveOrderFilterOptionsQuery';
import { getActiveOrderKey, mapActiveDistributionToRow } from '../../data/adapters/activeOrdersToRow';
import {
  buildActiveOrderDetailsParams,
  mergeDistributionWithOrderDetailsV2
} from '../../data/adapters/fulfillmentDrawer';
import { useOrderDetailsV2Query } from '../../data/useOrderDetailsV2Query';
import { useDeliveryTrackerQuery } from '../../data/useDeliveryTrackerQuery';
import { isDeliveryTrackerEligible, buildDeliveryTrackerRequest } from '../../data/adapters/deliveryTracker';
import { RawFulfillmentGroup } from '../../data/types';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];
const DATE_RANGE_LABEL = 'Pickup by';

// Single source of truth for the demo identity docs/chapters/active-orders-table's chapter-mocks.ts
// and examples/Example.tsx (and its error/empty variants) pass into this component's
// customerAccountId/purchaserId props, so every consumer of this fixture identity stays in sync.
export const MOCK_CUSTOMER_ACCOUNT_ID = 'mock-account-1';
export const MOCK_PURCHASER_ID = 'mock-purchaser-1';

export interface ActiveOrdersTableProps {
  /**
   * Identifies the signed-in customer for the activeOrders query. In production, omit these —
   * they're sourced automatically from useThdCustomer() (@thd-olt-functional/customer-information),
   * the same way AllOrdersTable sources its own identity. Pass them explicitly only to override
   * that (e.g. docs/tests) — an explicit value always wins over useThdCustomer().
   */
  customerAccountId?: string;
  /**
   * purchaserId mirrors AllOrdersTable's own userId -> useThdCustomer().userID mapping — flagged
   * in ADR-003 as an assumption pending confirmation, since activeOrders' request argument is
   * named purchaserId, not userId.
   */
  purchaserId?: string;
  /** Forwarded as request headers, matching AllOrdersTable's own convention. */
  sessionId?: string;
  channelId?: string;
  /** Page-size choices offered by the pagination bar's "N / Page" dropdown. Defaults to [10, 25, 50]; an explicit value overrides the default entirely. */
  pageSizeOptions?: number[];
  initialPageSize?: number;
  /**
   * Called whenever the account's overall active-order count (totalCardCount) is available or
   * changes — the full delivery+pickup total, not scoped to the current Deliveries/Pickups tab or
   * page. Not called while the query is loading/erroring.
   */
  onTotalOrdersChange?: (totalOrders: number) => void;
  /** Forwarded as-is to the fulfillment drawer's `isMobile` prop (see src/components/common/FulfillmentDrawer) — the drawer has no way to derive this itself. */
  isMobile?: boolean;
}

/**
 * Active Orders, connected to the activeOrders GraphQL API — see ADR-003.
 *
 * Unlike AllOrdersTable's purchaseHistory/orderDetails split, activeOrders' activeDistributions[]
 * already includes everything a row needs inline (recipient/location/instructions/lineItems) —
 * there is no second per-row query, so this wrapper has no row-enrichment machinery (no
 * useRowsInView/OrderDetailsFetcher/ExpandedOrderDetailsPanel equivalents).
 *
 * Composes the internal, non-exported ActiveOrdersTablePresentational shell for all rendering
 * (columns, toolbar, pagination).
 */
export function ActiveOrdersTable({
  customerAccountId: customerAccountIdProp,
  purchaserId: purchaserIdProp,
  sessionId,
  channelId,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  initialPageSize = DEFAULT_PAGE_SIZE,
  onTotalOrdersChange,
  isMobile
}: ActiveOrdersTableProps) {
  const thdCustomer = useThdCustomer();
  const customerAccountId = customerAccountIdProp ?? thdCustomer?.svocID ?? '';
  const purchaserId = purchaserIdProp ?? thdCustomer?.userID ?? '';
  const hasCustomerIdentity = Boolean(customerAccountId && purchaserId);

  // "Pickup by" presets/default are built into ActiveOrdersTable itself, not host-configurable
  // via props — mirrors AllOrdersTable's "Date Placed" treatment (see ADR-002/ADR-003).
  const dateRangePresets = useMemo(() => buildActiveOrdersDateRangePresets(), []);

  // ActiveOrdersTablePresentational only shows the Deliveries/Pickups toggle at all when
  // fulfillmentFilterValue is truthy (it's a two-tab view, not an optional/hideable filter) — so
  // this must default to one of the two real option keys, matching the real "Active Deliveries &
  // Pickups" experience's own default active tab (Deliveries), not an empty/unselected value.
  const [requestPayload, setRequestPayload] = useState<ActiveOrdersRequestPayload>({
    // search: '', cannot be used until backend contract supports it
    dateRange: buildDefaultActiveOrdersDateRange(),
    facets: {},
    fulfillmentType: 'DELIVERY',
    page: 1,
    pageSize: initialPageSize
  });

  // The two contract-defined filter entries (distributionType, distributionDate), plus one
  // {name, value} entry per facet selection. The filter shape is singular {name, value}, not
  // purchaseHistory's {name, values: []} — a facet with multiple selected values emits one entry per
  // value, flagged unconfirmed against the live schema (see ADR-003).
  //
  // distributionDate is how this API takes a date window — there are no startDate/endDate fields on
  // ActiveOrdersRequest at all (confirmed 2026-08-12; an earlier revision sent dates instead, which
  // was wrong). Its value must be one of exactly 'ALL' | 'Today' | 'Tomorrow' | 'Next 7 Days', so the
  // internal preset slug is translated through ACTIVE_ORDERS_DATE_SENTINEL rather than sent raw —
  // 'next7Days' is not a value this API recognizes.
  //
  // DateRangeFilter's custom-range flow sets dateRange.preset to the literal 'custom', which has no
  // sentinel because the API cannot express an arbitrary range. It falls back to 'ALL' — the widest
  // window, so the user's chosen dates are never silently narrowed away, only left un-narrowed.
  // **This means a custom range does nothing server-side**, which is a real UX gap: the picker is
  // part of the shared DateRangeFilter and cannot currently be hidden for this table alone. Tracked
  // as an open product question in ADR-003.
  //
  // distributionType is a view selection rather than a user-applied filter — the Deliveries/Pickups
  // toggle is a required two-tab view that always carries a value (defaulting to DELIVERY), and the
  // server-side filter is what keeps returned rows matching the tab's own column set.
  const filters = useMemo<ActiveOrdersRequestFilter[]>(() => {
    const facetFilters = Object.entries(requestPayload.facets).flatMap(([name, values]) =>
      values.map((value) => ({ name, value }))
    );
    const preset = requestPayload.dateRange.preset;
    return [
      { name: 'distributionType', value: requestPayload.fulfillmentType || 'ALL' },
      { name: 'distributionDate', value: (preset && ACTIVE_ORDERS_DATE_SENTINEL[preset]) || 'ALL' },
      ...facetFilters
    ];
  }, [requestPayload.facets, requestPayload.fulfillmentType, requestPayload.dateRange.preset]);

  // No startDate/endDate here: they are not fields on ActiveOrdersRequest (confirmed 2026-08-12).
  // The date window travels entirely through the distributionDate filter sentinel built above.
  //
  // searchValue/sortBy/sortOrder are deliberately NOT sent (2026-09-21): order-management-svc's
  // schema.graphqls confirms `ActiveOrdersRequest` has no such fields, so including them coerces
  // to a variable error and fails the whole request — exactly the failure mode
  // activeOrdersDataModel.ts's header comment warned about. The search box is therefore not wired
  // to this table below (no searchValue/onSearchSubmit props passed to
  // ActiveOrdersTablePresentational) until a real backend contract exists. Sorting has been
  // removed outright, not just unwired: ActiveOrdersTablePresentational no longer accepts
  // sortBy/sortDirection/onSortChange, and its own column definitions no longer mark themselves
  // sortable.
  //
  // A top-level `userId` IS required here despite order-management-svc's checked-in
  // schema.graphqls not declaring it as an argument on `activeOrders` (confirmed on `develop`,
  // 2026-09-22 — see concerns.instructions.md C6): a real request without it fails, confirmed
  // directly against the live API (2026-09-22). Treat the SDL file as out of sync with the actual
  // deployed contract for this one argument, not as ground truth to "fix" this back out. Sent as
  // `purchaserId` (the same resolved identity as `request.purchaserId` above), not
  // `thdCustomer?.userID` directly — a real, working sample request has `userId` exactly equal to
  // `request.purchaserId`, and reading `thdCustomer?.userID` here would silently diverge from
  // `request.purchaserId` whenever an explicit `purchaserId` prop overrides `useThdCustomer()`.
  const activeOrdersVariables = useMemo<ActiveOrdersQueryVariables>(
    () => ({
      request: {
        customerAccountId,
        purchaserId,
        pageSize: requestPayload.pageSize,
        pageNumber: requestPayload.page,
        filters
      },
      headers: { tmxProfileId: sessionId, channelId },
      userId: purchaserId
    }),
    [customerAccountId, purchaserId, requestPayload.pageSize, requestPayload.page, filters, sessionId, channelId]
  );

  const activeOrdersResult = useActiveOrdersQuery(activeOrdersVariables, { skip: !hasCustomerIdentity });
  const rawDistributions = useMemo(
    () => (activeOrdersResult.status === 'success' ? (activeOrdersResult.data.activeDistributions ?? []) : []),
    [activeOrdersResult]
  );

  // No direct pagination-total field exists on the confirmed response — totalCardCount/
  // totalDeliveryCount/totalPickupCount are fulfillment-count summaries, not a page total. This
  // interim fallback is flagged unconfirmed against the live schema in ADR-003.
  const totalItems = useMemo(() => {
    if (activeOrdersResult.status !== 'success') {
      return 0;
    }
    const { totalDeliveryCount, totalPickupCount } = activeOrdersResult.data;
    if (requestPayload.fulfillmentType === 'PICKUP') {
      return totalPickupCount ?? rawDistributions.length;
    }
    if (requestPayload.fulfillmentType === 'DELIVERY') {
      return totalDeliveryCount ?? rawDistributions.length;
    }
    return (totalDeliveryCount ?? 0) + (totalPickupCount ?? 0);
  }, [activeOrdersResult, requestPayload.fulfillmentType, rawDistributions.length]);

  // Live counts from the query result drive the Deliveries/Pickups toggle instead of a static
  // mock. Per explicit product direction, both counts must always show the account's real full
  // totals — a user on the Deliveries tab must still see the true Pickups count, not 0 — so this
  // relies on the backend returning totalDeliveryCount/totalPickupCount as stable full totals
  // regardless of which distributionType filter was requested, not counts scoped to that filter.
  // That expectation is unconfirmed against the live schema (see ADR-003) — if the real API scoops
  // these to the requested filter instead, this toggle will incorrectly show 0 for the inactive
  // tab and needs revisiting (e.g. a second, always-'ALL' counts-only request).
  // totalCardCount has no UI slot today — surfaced only via onTotalOrdersChange (see ADR-003).
  useEffect(() => {
    if (activeOrdersResult.status === 'success') {
      onTotalOrdersChange?.(activeOrdersResult.data.totalCardCount ?? 0);
    }
  }, [activeOrdersResult, onTotalOrdersChange]);

  const fulfillmentFilterOptions = useMemo<SegmentedFacetOption[]>(() => {
    if (activeOrdersResult.status !== 'success') {
      return [];
    }
    return [
      { key: 'DELIVERY', label: 'Deliveries', count: activeOrdersResult.data.totalDeliveryCount ?? 0 },
      { key: 'PICKUP', label: 'Pickups', count: activeOrdersResult.data.totalPickupCount ?? 0 }
    ];
  }, [activeOrdersResult]);

  const rows = useMemo(() => rawDistributions.map(mapActiveDistributionToRow), [rawDistributions]);

  // Keyed the same way as ActiveOrdersTablePresentational's own PurchaseHistoryTable `rowKey`
  // (`${orderNumber}-${distributionType}`, see getActiveOrderKey) so a clicked row's key can be
  // resolved back to the RAW distribution the fulfillment drawer needs — the mapped
  // PurchaseHistoryRow has already dropped fields (e.g. instructions) the drawer may want.
  const distributionsByKey = useMemo(
    () => new Map(rawDistributions.map((distribution) => [getActiveOrderKey(distribution), distribution])),
    [rawDistributions]
  );
  const [openOrderKey, setOpenOrderKey] = useState<string | undefined>(undefined);
  const selectedDistribution = openOrderKey ? distributionsByKey.get(openOrderKey) : undefined;

  // Only built (and only fetched) once the drawer is actually open for a given order — mirrors
  // this repo's "fetch orderDetails only for what's actually needed" discipline (elsewhere bounded
  // by scroll-into-view; here bounded by the drawer being open). Returns null when the selected
  // distribution doesn't carry enough identifying data for either orderDetails-v2 variant (see
  // buildActiveOrderDetailsParams), in which case the query below is skipped entirely.
  const orderDetailsV2Params = selectedDistribution
    ? buildActiveOrderDetailsParams(selectedDistribution, {
        userId: purchaserId,
        customerAccountId,
        tmxProfileId: sessionId,
        channelId
      })
    : null;
  const orderDetailsV2Result = useOrderDetailsV2Query(hasCustomerIdentity ? orderDetailsV2Params : null);
  const fulfillmentGroup = selectedDistribution
    ? mergeDistributionWithOrderDetailsV2(
        selectedDistribution,
        orderDetailsV2Result.status === 'success' ? orderDetailsV2Result.data : undefined
      )
    : undefined;

  // Delivery-tracker status/links are NOT part of orderDetails-v2 (see orderDetailsV2DataModel.ts
  // and types.ts's RawOrderDetailsV2Response header comments) — they come from the SEPARATE
  // deliveryTracker REST endpoint, exactly as they already do for AllOrdersTable's
  // ExpandedOrderDetailsPanel (see that file). `trackingNumber`/`workOrderNumber` are read off the
  // raw distribution itself (already-existing RawActiveDistribution fields, unrelated to OOP-4197);
  // `fulfillmentType`/`lineItems[0].statusDescription` come from the merged fulfillmentGroup above,
  // so eligibility naturally stays false until orderDetails-v2 has actually resolved a
  // fulfillmentType.
  const deliveryTrackerEligibilityGroup: RawFulfillmentGroup = {
    fulfillmentType: fulfillmentGroup?.fulfillmentType,
    trackingNumber: selectedDistribution?.trackingNumber,
    workOrderNumber: selectedDistribution?.workOrderNumber,
    lineItems: fulfillmentGroup?.lineItems?.length
      ? [{ statusDescription: fulfillmentGroup.lineItems[0]?.statusDescription }]
      : undefined
  };
  const isTrackerEligible = isDeliveryTrackerEligible(deliveryTrackerEligibilityGroup);
  const deliveryTrackerRequest =
    isTrackerEligible && selectedDistribution
      ? buildDeliveryTrackerRequest(
          deliveryTrackerEligibilityGroup,
          selectedDistribution.orderNumber ?? '',
          customerAccountId
        )
      : null;
  const deliveryTrackerResult = useDeliveryTrackerQuery(purchaserId, deliveryTrackerRequest, {
    skip: !selectedDistribution || !isTrackerEligible
  });
  const fulfillmentGroupWithTracker = fulfillmentGroup
    ? {
        ...fulfillmentGroup,
        deliveryTrackerData: deliveryTrackerResult.status === 'success' ? deliveryTrackerResult.data : undefined
      }
    : undefined;

  // Project/PO-Job Name facet options come from their own dedicated API — see
  // useActiveOrderFilterOptionsQuery — never derived from activeOrders' already-loaded rows.
  const filterOptionsResult = useActiveOrderFilterOptionsQuery(
    { purchaserId, customerAccountId },
    { skip: !hasCustomerIdentity }
  );
  const filterOptions =
    filterOptionsResult.status === 'success' ? filterOptionsResult.data : EMPTY_ACTIVE_ORDER_FILTER_OPTIONS;

  const facetGroupsFromApi = useMemo<FacetGroup[]>(
    () =>
      hasCustomerIdentity
        ? [
            {
              key: 'project',
              title: 'Project',
              options: filterOptions.project.map((name) => ({ label: name, value: name })),
              selectedValues: []
            },
            {
              key: 'poJobName',
              title: 'PO/Job Name',
              options: filterOptions.poJobName.map((name) => ({ label: name, value: name })),
              selectedValues: []
            }
          ]
        : [],
    [hasCustomerIdentity, filterOptions]
  );
  const facetGroupsWithSelections = useMemo(
    () =>
      facetGroupsFromApi.map((group) => ({
        ...group,
        selectedValues: requestPayload.facets[group.key] ?? group.selectedValues
      })),
    [facetGroupsFromApi, requestPayload.facets]
  );

  const handleFacetsApply = (facets: FacetSelections) =>
    setRequestPayload((previous) => ({ ...previous, facets, page: 1 }));

  return (
    <>
      <ActiveOrdersTablePresentational
        rows={rows}
        onDateCellClick={setOpenOrderKey}
        openOrderKey={openOrderKey}
        isLoading={activeOrdersResult.status === 'loading' || activeOrdersResult.status === 'idle'}
        isError={activeOrdersResult.status === 'error'}
        onRetry={activeOrdersResult.status === 'error' ? activeOrdersResult.refetch : undefined}
        dateRangeLabel={DATE_RANGE_LABEL}
        dateRangeValue={requestPayload.dateRange}
        dateRangePresets={dateRangePresets}
        onDateRangeApply={(dateRange) => setRequestPayload((previous) => ({ ...previous, dateRange, page: 1 }))}
        facetGroups={facetGroupsWithSelections}
        onFacetsApply={handleFacetsApply}
        isFacetsLoading={filterOptionsResult.status === 'loading'}
        facetsError={filterOptionsResult.status === 'error'}
        fulfillmentFilterOptions={fulfillmentFilterOptions}
        fulfillmentFilterValue={requestPayload.fulfillmentType}
        onFulfillmentFilterChange={(fulfillmentType) =>
          setRequestPayload((previous) => ({ ...previous, fulfillmentType, page: 1 }))
        }
        totalItems={totalItems}
        page={requestPayload.page}
        pageSize={requestPayload.pageSize}
        onPageChange={(page) => setRequestPayload((previous) => ({ ...previous, page }))}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={(pageSize) => setRequestPayload((previous) => ({ ...previous, pageSize, page: 1 }))}
      />
      {/*
        FulfillmentDetailsDrawer (src/components/common/FulfillmentDrawer — a one-to-one local
        port of the active-orders-carousel effort's own drawer, see OOP-4059 and that directory's
        doc comment for why it's a port rather than a package import) is purely presentational and
        expects a flat `fulfillmentGroup` object.
        `fulfillmentGroupWithTracker` (OOP-4197) is built in two steps: (1)
        `mergeDistributionWithOrderDetailsV2` overlays the raw activeOrders distribution with a
        second, drawer-only orderDetails-v2 query (useOrderDetailsV2Query) fired only while the
        drawer is open — see src/data/adapters/fulfillmentDrawer.ts; (2) a THIRD, separate REST
        call (useDeliveryTrackerQuery, same endpoint AllOrdersTable's ExpandedOrderDetailsPanel
        already uses) supplies `deliveryTrackerData` — that field is deliberately NOT part of
        orderDetails-v2 (see orderDetailsV2DataModel.ts's own comment on why). Together these
        resolve the drawer's long-standing known gap (fulfillmentType/storePickup/
        bodfsDeliveryAndHandling/shippingAndHandling/lineItems[].statusCode/deliveryTrackerData
        were never in the activeOrders response) for ONLINE orders. KNOWN REMAINING GAP:
        storeNumber/transactionId/registerNumber/salesDate/orderOrigin were all CONFIRMED
        REJECTED by the real stage schema (removed from activeOrdersDataModel.ts's selection —
        see that file's and types.ts's RawActiveDistribution header comments) — a real
        distribution never carries them, so buildActiveOrderDetailsParams' 'inStore' variant is
        effectively unreachable in production, and the drawer falls back to the raw
        distribution's own fields for in-store/pickup orders, same as before this integration. A
        failed/loading/v1-shaped orderDetails-v2 or deliveryTracker result degrades
        the same way, never blocking the drawer itself.
      */}
      <FulfillmentDetailsDrawer
        open={Boolean(selectedDistribution)}
        onClose={() => setOpenOrderKey(undefined)}
        fulfillmentGroup={fulfillmentGroupWithTracker}
        isMobile={isMobile}
      />
    </>
  );
}
